import { ApiCostsDataResponse, DailyCostData } from '../types/api';
import { TableData, TableColumn } from '../components/GenericTable';
import { ChartDataset } from '../components/GenericBarChart';
import { CurveDataset } from '../components/GenericCurveChart';

const parseDailyData = (dailyData: { [key: string]: string }): Array<{
  date: string;
  dateKey: string;
  plannedCost: number;
  executedCost: number;
  color: string;
  isWeekend: boolean;
  weekState: number;
}> => {
  const result = [];

  for (const [key, value] of Object.entries(dailyData)) {
    try {
      const data: DailyCostData = JSON.parse(value);
      const dateMatch = key.match(/Date_(\d{4})(\d{2})(\d{2})/);
      if (dateMatch) {
        const [, year, month, day] = dateMatch;
        const date = `${year}-${month}-${day}`;
        
        result.push({
          date,
          dateKey: key,
          plannedCost: data.PlannedCost || 0,
          executedCost: data.ExecutedCost || 0,
          color: data.Color || '',
          isWeekend: data.IsWeekend || false,
          weekState: data.WeekState || 1
        });
      }
    } catch (error) {
      console.warn('Error parsing daily data for key:', key, error);
    }
  }
  return result.sort((a, b) => a.date.localeCompare(b.date));
};

const parseWeeklyData = (weeklyData: { [key: string]: string }): Array<{
  weekNumber: string;
  weekKey: string;
  startDate: string;
  endDate: string;
  dateRange: string;
  plannedCost: number;
  executedCost: number;
  color: string;
  isWeekend: boolean;
  weekState: number;
}> => {
  const result = [];

  for (const [key, value] of Object.entries(weeklyData)) {
    try {
      const data: DailyCostData = JSON.parse(value);
      const weekMatch = key.match(/Semana_(\d+)_(\d{4})(\d{2})(\d{2})_(\d{4})(\d{2})(\d{2})/);
      if (weekMatch) {
        const [, weekNum, startYear, startMonth, startDay, endYear, endMonth, endDay] = weekMatch;
        
        const startDate = `${startYear}-${startMonth}-${startDay}`;
        const endDate = `${endYear}-${endMonth}-${endDay}`;
        const dateRange = `${startDay}/${startMonth}/${startYear} - ${endDay}/${endMonth}/${endYear}`;
        
        result.push({
          weekNumber: `Semana ${weekNum}`,
          weekKey: key,
          startDate,
          endDate,
          dateRange,
          plannedCost: data.PlannedCost || 0,
          executedCost: data.ExecutedCost || 0,
          color: data.Color || '',
          isWeekend: data.IsWeekend || false,
          weekState: data.WeekState || 1
        });
      }
    } catch (error) {
      console.warn('Error parsing weekly data for key:', key, error);
    }
  }
  return result.sort((a, b) => a.startDate.localeCompare(b.startDate));
};

export const transformCostsTableData = (
  data: ApiCostsDataResponse,
  dateRange: { start: string; end: string } = { start: '', end: '' },
  period: 'day' | 'week' = 'day'
): TableData[] => {
  let tableData: TableData[] = [];

  if (period === 'day') {
    const dailyData = parseDailyData(data.DailyData);
    
    let filteredData = dailyData;
    if (dateRange.start && dateRange.end) {
      const parseDateFromDDMMYYYY = (dateStr: string): Date => {
        const [day, month, year] = dateStr.split('/').map(Number);
        return new Date(year, month - 1, day);
      };

      const startDate = parseDateFromDDMMYYYY(dateRange.start);
      const endDate = parseDateFromDDMMYYYY(dateRange.end);

      filteredData = dailyData.filter(item => {
        const [year, month, day] = item.date.split('-').map(Number);
        const itemDate = new Date(year, month - 1, day);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    tableData = filteredData.map(item => {
      const formattedDate = formatDateForDisplay(item.date);
      
      return {
        id: item.dateKey,
        key: item.dateKey,
        Fecha: formattedDate,
        'Costo Planeado': item.plannedCost,
        'Costo Ejecutado': item.executedCost,
        'Diferencia': (item.executedCost - item.plannedCost),
        _metadata: {
          backgroundColor: item.color || '#ffffff',
          textColor: item.color ? getContrastColor(item.color) : '#000000',
          isWeekend: item.isWeekend,
          weekState: item.weekState
        }
      };
    });
  } else {
    const weeklyData = parseWeeklyData(data.DailyData);
    
    let filteredData = weeklyData;
    if (dateRange.start && dateRange.end) {
      const parseDateFromDDMMYYYY = (dateStr: string): Date => {
        const [day, month, year] = dateStr.split('/').map(Number);
        return new Date(year, month - 1, day);
      };

      const startDate = parseDateFromDDMMYYYY(dateRange.start);
      const endDate = parseDateFromDDMMYYYY(dateRange.end);

      filteredData = weeklyData.filter(item => {
        const [startYear, startMonth, startDay] = item.startDate.split('-').map(Number);
        const itemStartDate = new Date(startYear, startMonth - 1, startDay);
        
        const [endYear, endMonth, endDay] = item.endDate.split('-').map(Number);
        const itemEndDate = new Date(endYear, endMonth - 1, endDay);
        return (itemStartDate <= endDate && itemEndDate >= startDate);
      });
    }

    tableData = filteredData.map(item => {
      return {
        id: item.weekKey,
        key: item.weekKey,
        Semana: item.weekNumber,
        'Rango Fechas': item.dateRange,
        'Costo Planeado': item.plannedCost,
        'Costo Ejecutado': item.executedCost,
        'Diferencia': (item.executedCost- item.plannedCost),
        _metadata: {
          backgroundColor: item.color || '#ffffff',
          textColor: item.color ? getContrastColor(item.color) : '#000000',
          isWeekend: item.isWeekend,
          weekState: item.weekState,
          dateRange: item.dateRange
        }
      };
    });
  }

  return tableData;
};
interface ChartDataResponse {
  labels: string[];
  datasets: ChartDataset[];
  maxValue: number;
  shouldUseHorizontalLabels: boolean;
  customOptions?: any;
}

export const transformCostsChartData = (
  data: ApiCostsDataResponse,
  dateRange: { start: string; end: string } = { start: '', end: '' },
  period: 'day' | 'week' = 'day'
): ChartDataResponse => {
  let labels: string[] = [];
  let plannedCostData: number[] = [];
  let executedCostData: number[] = [];
  let dateRanges: string[] = [];

  if (period === 'day') {
    const dailyData = parseDailyData(data.DailyData);
    
    let filteredData = dailyData;
    if (dateRange.start && dateRange.end) {
      const parseDateFromDDMMYYYY = (dateStr: string): Date => {
        const [day, month, year] = dateStr.split('/').map(Number);
        return new Date(year, month - 1, day);
      };

      const startDate = parseDateFromDDMMYYYY(dateRange.start);
      const endDate = parseDateFromDDMMYYYY(dateRange.end);

      filteredData = dailyData.filter(item => {
        const [year, month, day] = item.date.split('-').map(Number);
        const itemDate = new Date(year, month - 1, day);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }
    labels = filteredData.map(item => formatDateForDisplay(item.date));
    plannedCostData = filteredData.map(item => item.plannedCost);
    executedCostData = filteredData.map(item => item.executedCost);
  } else {
    const weeklyData = parseWeeklyData(data.DailyData);
    
    let filteredData = weeklyData;
    if (dateRange.start && dateRange.end) {
      const parseDateFromDDMMYYYY = (dateStr: string): Date => {
        const [day, month, year] = dateStr.split('/').map(Number);
        return new Date(year, month - 1, day);
      };

      const startDate = parseDateFromDDMMYYYY(dateRange.start);
      const endDate = parseDateFromDDMMYYYY(dateRange.end);

      filteredData = weeklyData.filter(item => {
        const [startYear, startMonth, startDay] = item.startDate.split('-').map(Number);
        const itemStartDate = new Date(startYear, startMonth - 1, startDay);
        
        const [endYear, endMonth, endDay] = item.endDate.split('-').map(Number);
        const itemEndDate = new Date(endYear, endMonth - 1, endDay);
        
        return (itemStartDate <= endDate && itemEndDate >= startDate);
      });
    }

    labels = filteredData.map(item => item.weekNumber);
    plannedCostData = filteredData.map(item => item.plannedCost);
    executedCostData = filteredData.map(item => item.executedCost);
    dateRanges = filteredData.map(item => item.dateRange);
  }

  const itemCount = labels.length;
  const shouldUseHorizontalLabels = itemCount <= 12;

  let calculatedMaxValue = 0;
  
  [...plannedCostData, ...executedCostData].forEach(value => {
    calculatedMaxValue = Math.max(calculatedMaxValue, value);
  });

  if (calculatedMaxValue === 0) {
    calculatedMaxValue = 100;
  } else {
    calculatedMaxValue = Math.floor(calculatedMaxValue * 1.1);
  }

  const datasets: ChartDataset[] = [
    {
      label: 'Costo Planeado',
      data: plannedCostData,
      backgroundColor: 'rgba(54, 162, 235, 0.8)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 2,
    },
    {
      label: 'Costo Ejecutado',
      data: executedCostData,
      backgroundColor: 'rgba(75, 192, 192, 0.8)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 2,
    },
  ];

  const customOptions = {
    plugins: {
      tooltip: {
        callbacks: {
          title:  period === 'week' 
          ? function(context: any) {
              const index = context[0].dataIndex;
              if (dateRanges[index]) {
                return [`${dateRanges[index]}`];
              }
              return `Semana: ${labels[index]}`;
            }
          : undefined,
          afterBody: function(context: any) {
            const index = context[0].dataIndex;
            // if (dateRanges[index]) {
            //   return [`Rango: ${dateRanges[index]}`];
            // }
            // return [];
            const difference =Number(executedCostData[index]) - Number(plannedCostData[index]);
            return [`Diferencia: ${difference}`]
            
          }
        }
      }
    }
  };

  return {
    labels,
    datasets,
    maxValue: calculatedMaxValue,
    shouldUseHorizontalLabels,
    customOptions
  };
};

interface CurveDataResponse {
  labels: string[];
  datasets: CurveDataset[];
  maxValue: number;
  shouldUseHorizontalLabels: boolean;
  customOptions?: any;
}

export const transformCostsCurveData = (
  data: ApiCostsDataResponse,
  dateRange: { start: string; end: string } = { start: '', end: '' },
  period: 'week' | 'day'
): CurveDataResponse => {
  const isWeekly = period === 'week';
  
  if (isWeekly) {
    console.log('📊 Curva S: Datos semanales detectados, no se aplicará filtro de fechas');
  }

  const parseSCurveData = (curveData: string): Array<{date?: string; week?: string; value: number}> => {
    try {
      const parsed = JSON.parse(curveData) || [];
      return parsed.map((item: any) => ({
        date: item.date,
        week: item.week,
        value: item.value
      }));
    } catch (error) {
      console.warn('Error parsing S-curve data:', error);
      return [];
    }
  };

  const plannedCurve = parseSCurveData(data.SCurvePlannedCost);
  const executedCurve = parseSCurveData(data.SCurveExecutedCost);

  let filteredPlanned = plannedCurve;
  let filteredExecuted = executedCurve;
  
  if (!isWeekly && dateRange.start && dateRange.end) {
    const normalizeDate = (dateStr: string): Date => {
      if (dateStr.includes('-')) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    };

    const startDate = normalizeDate(dateRange.start);
    const endDate = normalizeDate(dateRange.end);

    filteredPlanned = plannedCurve.filter(item => {
      if (!item.date) return true;
      const itemDate = normalizeDate(item.date);
      const isInRange = itemDate >= startDate && itemDate <= endDate;
      return isInRange;
    });

    filteredExecuted = executedCurve.filter(item => {
      if (!item.date) return true;
      const itemDate = normalizeDate(item.date);
      const isInRange = itemDate >= startDate && itemDate <= endDate;
      return isInRange;
    });
  }

  if (filteredPlanned.length === 0 && plannedCurve.length > 0) {
    filteredPlanned = plannedCurve;
  }

  if (filteredExecuted.length === 0 && executedCurve.length > 0) {
    filteredExecuted = executedCurve;
  }

  const labels = filteredPlanned.map(item => {
    if (isWeekly && item.week) {
      return item.week.replace(/_/g, ' ');
    }
    return item.date ? formatDateForDisplay(item.date) : '';
  }).filter(label => label !== '');

  const itemCount = labels.length;
  const shouldUseHorizontalLabels = itemCount <= 12;

  let calculatedMaxValue = 0;
  
  [...filteredPlanned, ...filteredExecuted].forEach(item => {
    calculatedMaxValue = Math.max(calculatedMaxValue, item.value);
  });

  if (calculatedMaxValue === 0) {
    calculatedMaxValue = 100;
  } else {
    calculatedMaxValue = Math.floor(calculatedMaxValue * 1.1);
  }

  const datasets: CurveDataset[] = [
    {
      label: 'Costo Planeado Acumulado',
      data: filteredPlanned.map(item => item.value),
      borderColor: 'rgba(54, 162, 235, 1)',
      backgroundColor: 'rgba(54, 162, 235, 0.2)',
      borderWidth: 2,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      fill: false,
    },
    {
      label: 'Costo Ejecutado Acumulado',
      data: filteredExecuted.map(item => item.value),
      borderColor: 'rgba(75, 192, 192, 1)',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      borderWidth: 2,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      fill: false,
    }
  ];
  
  const customOptions = {
    plugins: {
      tooltip: {
        callbacks: {
          afterBody: function(context: any) {
            const index = context[0].dataIndex;
            // if (dateRanges[index]) {
            //   return [`Rango: ${dateRanges[index]}`];
            // }
            // return [];
            const difference =Number(filteredExecuted[index].value) - Number(filteredPlanned[index].value);
            return [`Diferencia: ${difference}`]
            
          }
        }
      }
    }
  };

  return {
    labels,
    datasets,
    maxValue: calculatedMaxValue,
    shouldUseHorizontalLabels,
    customOptions,
  };
};

const formatDateForDisplay = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const getContrastColor = (hexColor: string): string => {
  if (hexColor && hexColor.startsWith('#')) {
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#FFFFFF';
  }
  return '#000000';
};

export const costsDailyTableColumns: TableColumn[] = [
  { key: 'Fecha', label: 'Fecha', width: '25%' },
  { key: 'Costo Planeado', label: 'Costo Planeado', align: 'right', width: '25%' },
  { key: 'Costo Ejecutado', label: 'Costo Ejecutado', align: 'right', width: '25%' },
  { key: 'Diferencia', label: 'Diferencia', align: 'right', width: '25%' }
];

export const costsWeeklyTableColumns: TableColumn[] = [
  { key: 'Semana', label: 'Semana', width: '20%' },
  { key: 'Rango Fechas', label: 'Rango de Fechas', width: '25%' },
  { key: 'Costo Planeado', label: 'Costo Planeado', align: 'right', width: '18%' },
  { key: 'Costo Ejecutado', label: 'Costo Ejecutado', align: 'right', width: '18%' },
  { key: 'Diferencia', label: 'Diferencia', align: 'right', width: '19%' }
];

export const getCostsRowBackgroundColor = (row: TableData) => {
  return row._metadata?.backgroundColor || '#ffffff';
};

export const getCostsRowTextColor = (row: TableData) => {
  return row._metadata?.textColor || '#000000';
};

export const getCostsCellBackgroundColor = (row: TableData, columnKey: string) => {
  return getCostsRowBackgroundColor(row);
};

export const getCostsCellTextColor = (row: TableData, columnKey: string) => {
  return getCostsRowTextColor(row);
};